import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { PageHero } from '../components/PageHero';
import { Bot, Sparkles, Flame, TrendingUp, RefreshCw, Globe, Send, Lightbulb } from 'lucide-react';
import TelegramShareButton from '../components/TelegramShareButton';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['all', 'sports', 'crypto', 'politics', 'entertainment', 'custom'];

const CAT_COLORS = {
    sports: '#00FF66', crypto: '#FFD700', politics: '#9B6FE8',
    entertainment: '#FF6B00', custom: '#00FFFF',
};

export default function AIBetMasterPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [bets, setBets] = useState([]);
    const [stats, setStats] = useState(null);
    const [cat, setCat] = useState('all');
    const [generating, setGenerating] = useState(false);
    const [stakes, setStakes] = useState({});
    const [shareCounts, setShareCounts] = useState({});

    const load = async () => {
        try {
            const q = cat === 'all' ? '' : `&category=${cat}`;
            const [b, s] = await Promise.all([
                fetch(`${API}/api/ai-bets/list?status=open${q}&limit=50`).then(r => r.json()),
                fetch(`${API}/api/ai-bets/stats`).then(r => r.json()),
            ]);
            setBets(b.bets || []);
            setStats(s);
            // Bulk fetch share counts for all loaded bets
            const ids = (b.bets || []).map(x => x.bet_id);
            if (ids.length > 0) {
                const res = await fetch(`${API}/api/social-sharing/bets/counts`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ bet_ids: ids }),
                }).then(r => r.json());
                setShareCounts(res.counts || {});
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); }, [cat]);
    useEffect(() => { const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            // Background mode: returns job_id immediately
            const res = await fetch(`${API}/api/ai-bets/generate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ count: 5 }),
            });
            const data = await res.json();
            if (!data.success) {
                toast.error(data.error || 'Échec génération');
                setGenerating(false);
                return;
            }
            if (data.queued && data.job_id) {
                toast.info('Grok travaille... ~30-60s', { duration: 60000 });
                // Poll status every 4 seconds
                const poll = setInterval(async () => {
                    try {
                        const s = await fetch(`${API}/api/ai-bets/generation-status/${data.job_id}`).then(r => r.json());
                        if (s.status === 'done') {
                            clearInterval(poll);
                            const provider = s.result?.provider || '?';
                            const count = s.result?.count || 0;
                            toast.success(`${count} paris générés via ${provider}`);
                            setGenerating(false);
                            load();
                        } else if (s.status === 'failed') {
                            clearInterval(poll);
                            toast.error(`Échec: ${s.error || s.result?.error || 'inconnu'}`);
                            setGenerating(false);
                        }
                    } catch (e) { /* keep polling */ }
                }, 4000);
                // Hard timeout 3 minutes
                setTimeout(() => { clearInterval(poll); setGenerating(false); }, 180000);
            } else {
                // Sync result (sync=true case)
                toast.success(`IA a généré ${data.count} paris via ${data.provider || '?'}`);
                setGenerating(false);
                load();
            }
        } catch (e) { toast.error('Erreur génération'); setGenerating(false); }
    };

    const takeBet = async (betId) => {
        if (!address) return toast.error('Connectez votre wallet');
        const stake = parseFloat(stakes[betId] || 0);
        if (!stake || stake <= 0) return toast.error('Mise invalide');
        const res = await fetch(`${API}/api/ai-bets/take`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bet_id: betId, taker_address: address, stake_eth: stake }),
        });
        const data = await res.json();
        if (data.success) {
            toast.success(`Pari accepté: ${stake} ETH`);
            setStakes(s => ({ ...s, [betId]: '' }));
            load();
        } else toast.error(data.error);
    };

    return (
        <div className="min-h-screen" data-testid="ai-bet-master-page">
            <PageHero
                icon={Bot}
                title={t('aiBets.title')}
                highlight={t('aiBets.highlight')}
                subtitle={t('aiBets.subtitle')}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Stats row */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                        <Card className="bg-[#00FFFF]/5 border-[#00FFFF]/20">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Paris IA générés</div>
                                <div className="text-2xl font-bold text-[#00FFFF]" data-testid="ai-total-bets">{stats.total_ai_bets}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Ouverts</div>
                                <div className="text-2xl font-bold text-[#00FF66]">{stats.open}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Résolus</div>
                                <div className="text-2xl font-bold text-white">{stats.resolved}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#FFD700]/5 border-[#FFD700]/20">
                            <CardContent className="p-4">
                                <div className="text-xs text-white/50 uppercase">Frais collectés</div>
                                <div className="text-2xl font-bold text-[#FFD700]">{stats.total_fees_eth?.toFixed(6)} ETH</div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filters + Generate */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    {CATEGORIES.map(c => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${cat === c ? 'bg-[#00FF66] text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                            data-testid={`filter-${c}`}
                        >
                            {c === 'all' ? 'Tout' : c}
                        </button>
                    ))}
                    <div className="flex-1" />
                    <Button onClick={() => generate(true)} disabled={generating} className="bg-gradient-to-r from-black to-[#1DA1F2] text-white border border-[#1DA1F2]/40 hover:opacity-90" data-testid="generate-live-trends-btn">
                        <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-pulse' : ''}`} />
                        Live X Trends
                    </Button>
                    <Button onClick={() => generate(false)} disabled={generating} className="bg-[#00FFFF] text-black hover:bg-[#00FFFF]/80" data-testid="generate-ai-bets-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                        {generating ? 'Génération...' : 'Générer 5 paris IA'}
                    </Button>
                </div>

                {/* Bets Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                    {bets.length === 0 ? (
                        <Card className="md:col-span-2 bg-white/5 border-white/10"><CardContent className="p-8 text-center text-white/40">Aucun pari IA actif. Cliquez "Générer" pour faire travailler l'IA.</CardContent></Card>
                    ) : bets.map(b => (
                        <Card key={b.bet_id} className="bg-white/5 border-white/10 hover:border-[#00FFFF]/30 transition-all" data-testid={`ai-bet-${b.bet_id}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge style={{ color: CAT_COLORS[b.category], borderColor: `${CAT_COLORS[b.category]}50` }} className="bg-transparent">{b.category}</Badge>
                                        {b.is_live_trend && <Badge className="bg-[#1DA1F2]/15 text-[#1DA1F2] border-[#1DA1F2]/40 text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Live X</Badge>}
                                        {(shareCounts[b.bet_id] || 0) > 0 && (
                                            <Badge className="bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30 text-xs flex items-center gap-1" data-testid={`share-count-${b.bet_id}`}>
                                                <Send className="w-3 h-3" /> {shareCounts[b.bet_id]} {shareCounts[b.bet_id] === 1 ? 'partage' : 'partages'}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 text-[#FF6B00]">
                                        <Flame className="w-4 h-4" />
                                        <span className="text-sm font-bold">{b.viral_score}/10</span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-white font-medium mb-3">{b.description}</div>
                                <div className="text-xs text-white/50 mb-3 italic flex items-start gap-1.5"><Lightbulb className="w-3 h-3 text-[#FFD700] mt-0.5 shrink-0" /> {b.resolution_hint}</div>
                                <div className="flex items-center gap-3 text-xs text-white/50 mb-4">
                                    <span><TrendingUp className="inline w-3 h-3 mr-1" />Cote {(b.odds_bps / 10000).toFixed(2)}x</span>
                                    <span>·</span>
                                    <span>Résolution: {new Date(b.resolution_time).toLocaleDateString('fr-FR')}</span>
                                </div>
                                <div className="flex gap-2">
                                    <Input type="number" placeholder="ETH" step="0.001"
                                        value={stakes[b.bet_id] || ''}
                                        onChange={e => setStakes(s => ({ ...s, [b.bet_id]: e.target.value }))}
                                        className="bg-black/30 border-white/20 text-white h-9"
                                        data-testid={`ai-stake-${b.bet_id}`}
                                    />
                                    <Button onClick={() => takeBet(b.bet_id)} size="sm" className="bg-[#00FF66] text-black hover:bg-[#00FF66]/80" data-testid={`take-ai-bet-${b.bet_id}`}>
                                        Parier
                                    </Button>
                                    <TelegramShareButton
                                        title={b.description}
                                        subtitle={`${b.category} · cote ${(b.odds_bps / 10000).toFixed(2)}x · viral ${b.viral_score}/10${b.is_live_trend ? ' · Live X' : ''}`}
                                        path="/ai-bets"
                                        tone={b.is_live_trend ? 'gold' : 'cyan'}
                                        size="icon"
                                        testId={`share-ai-bet-${b.bet_id}`}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
}
